import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

const timeline = [
  { year: '2023', title: '18c.mic 项目启动', description: '创始团队洞察到企业资源管理与协作的痛点，正式启动18c.mic平台的研发工作。' },
  { year: '2024 Q1', title: 'Beta 版本发布', description: '完成核心功能开发，邀请首批种子用户进行内测，收集反馈快速迭代。' },
  { year: '2024 Q3', title: 'V1.0 正式上线', description: '18c.mic V1.0正式面向公众开放，凭借出色的用户体验迅速获得市场认可。' },
  { year: '2025 Q1', title: '用户突破10,000', description: '上线半年，注册用户突破万级，客户覆盖科技、教育、金融等多个行业。' },
  { year: '2025 Q3', title: 'V2.0 智能协作升级', description: '推出智能协作空间与数据洞察面板，平台能力实现质的飞跃。' },
  { year: '2026 Q1', title: '全球用户突破50,000', description: '平台覆盖120+国家和地区，成为国际化资源管理协作平台。' },
  { year: '2026 Q3', title: 'V3.0 重磅发布', description: '引入智能推荐引擎与自动化工作流，开启AI驱动的新篇章。' },
];

const values = [
  {
    icon: 'ri-focus-3-line',
    title: '用户至上',
    description: '以用户需求为产品核心驱动力，每一个功能都源于真实场景。',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: '创新驱动',
    description: '持续探索前沿技术，将AI和自动化深度融合到产品中。',
  },
  {
    icon: 'ri-shield-star-line',
    title: '安全可靠',
    description: '将数据安全视为产品生命线，构建全方位的安全防护体系。',
  },
  {
    icon: 'ri-hand-heart-line',
    title: '开放共赢',
    description: '拥抱开放生态，与合作伙伴和社区共同成长。',
  },
];

const testimonials = [
  {
    name: '张明',
    role: '某科技公司 CTO',
    content: '18c.mic彻底改变了我们团队的协作方式。从资源散落各处到统一管理，效率提升了不止一个档次。',
    rating: 5,
  },
  {
    name: '李婷',
    role: '创业团队创始人',
    content: '作为5人小团队，18c.mic的性价比无可挑剔。上手简单，功能强大，陪伴我们从0到1的每一步。',
    rating: 5,
  },
  {
    name: '王磊',
    role: '企业信息化负责人',
    content: '安全性是我们选择平台的首要标准，18c.mic的权限控制和审计日志让我们非常放心。',
    rating: 5,
  },
  {
    name: '赵雪',
    role: '远程团队管理者',
    content: '跨国远程协作从未如此顺畅。18c.mic让分散在三个大洲的团队像坐在同一个办公室一样高效。',
    rating: 5,
  },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }
    try {
      const res = await fetch('https://form.18c.mic/api/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Object.fromEntries(
            Array.from(formData.entries())
              .filter(([k]) => k !== 'phone_alt')
              .map(([k, v]) => [k, String(v)])
          )
        ).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
        || parsed?.message as string
        || '';
      const codeOk = parsed?.code === 'OK';
      if (res.ok && codeOk) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <main>
        <Navbar />

        <section className="pt-24 pb-14 bg-background-50">
          <div className="mx-auto px-4 md:px-6 max-w-[1400px]">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
            <div className="text-center max-w-3xl mx-auto mt-8">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-6">
                关于 18c.mic
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                18c.mic 诞生于2023年，致力于打造全球领先的资源管理与智能协作平台。
                我们相信，优秀的工具能够释放团队的无限潜能。
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="mx-auto px-4 md:px-6 max-w-[1400px]">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
                发展历程
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                我们的故事
              </h2>
            </div>
            <div className="max-w-3xl mx-auto relative">
              <div className="absolute left-[19px] top-0 bottom-0 w-px bg-background-200"></div>
              <div className="space-y-10">
                {timeline.map((item, i) => (
                  <div key={i} className="relative pl-12">
                    <div className="absolute left-[11px] top-1 w-4 h-4 rounded-full bg-primary-500 border-4 border-background-50"></div>
                    <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                      <span className="text-xs font-semibold text-primary-600 bg-primary-100 px-2.5 py-0.5 rounded-full">
                        {item.year}
                      </span>
                      <h3 className="text-base font-semibold text-foreground-950 mt-2 mb-1">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="mx-auto px-4 md:px-6 max-w-[1400px]">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
                核心价值观
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                驱动我们的信念
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((val) => (
                <div
                  key={val.title}
                  className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center"
                >
                  <div className="w-12 h-12 mx-auto flex items-center justify-center bg-accent-100 rounded-full mb-4">
                    <i className={`${val.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2 whitespace-nowrap">
                    {val.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {val.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="mx-auto px-4 md:px-6 max-w-[1400px]">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-4">
                用户口碑
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                用户怎么说
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="p-6 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm text-accent-500"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "{t.content}"
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="mx-auto px-4 md:px-6 max-w-[1400px]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
              <div>
                <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
                  联系我们
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  与我们取得联系
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                  无论您有任何问题、建议或合作意向，都欢迎随时联系我们。我们的团队将在24小时内回复。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">电子邮箱</p>
                      <p className="text-sm text-foreground-600">contact@18c.mic</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">联系电话</p>
                      <p className="text-sm text-foreground-600">400-888-1818</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">办公地址</p>
                      <p className="text-sm text-foreground-600">北京市朝阳区科技园路18号 创新大厦12层</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                <h3 className="text-lg font-semibold text-foreground-950 mb-6">
                  发送消息
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form>
                  <input
                    type="text"
                    name="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <div>
                    <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息内容 <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您想咨询的内容（最多500字）"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="whitespace-nowrap w-full py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    发送消息
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-primary-600 text-center">消息已发送成功，我们将尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-accent-600 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        <Footer />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于 18c.mic',
              description: '了解18c.mic的品牌故事、发展历程、核心价值观与口碑评价。',
              url: 'https://18c.mic/about',
              mainEntity: {
                '@type': 'Organization',
                name: '18c.mic',
                url: 'https://18c.mic',
                foundingDate: '2023',
                contactPoint: {
                  '@type': 'ContactPoint',
                  telephone: '400-888-1818',
                  contactType: 'customer service',
                  email: 'contact@18c.mic',
                },
                address: {
                  '@type': 'PostalAddress',
                  streetAddress: '科技园路18号 创新大厦12层',
                  addressLocality: '北京市',
                  addressRegion: '朝阳区',
                  addressCountry: 'CN',
                },
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://18c.mic',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: '关于我们',
                },
              ],
            }),
          }}
        />
      </main>
    </>
  );
}